import React from 'react';
import PropTypes from 'prop-types';
import clsx from 'clsx';

import { makeStyles, useTheme } from '@material-ui/core/styles';
import useMediaQuery from '@material-ui/core/useMediaQuery';

import { getRedesignDialogColors } from './redesignDialogColors';
import { REDESIGN_DIALOG_SPLIT_MAIN_WIDTH } from './redesignDialogStyles';

/**
 * The most of the split's width the main pane takes on a medium screen,
 * whatever `mainWidth` asks: the aside (a PDF) keeps the larger half.
 */
export const REDESIGN_DIALOG_SPLIT_MAIN_MAX = '45%';

const useStyles = makeStyles(
  (theme) => {
    const colors = getRedesignDialogColors(theme);
    const xs = theme.breakpoints.down('xs');
    const scrolling = {
      minHeight: 0,
      overflowY: 'auto',
      overscrollBehavior: 'contain',
      WebkitOverflowScrolling: 'touch',
    };
    return {
      // From md up: side by side, as tall as the dialog's body, each pane
      // scrolling on its own.
      sideBySide: {
        display: 'flex',
        alignItems: 'stretch',
        flex: '1 1 auto',
        minHeight: 0,
      },
      // Below md: one column, main first, scrolled by the dialog's body.
      stacked: {
        display: 'flex',
        flexDirection: 'column',
        flex: '0 0 auto',
      },
      main: {
        boxSizing: 'border-box',
        minWidth: 0,
        padding: theme.spacing(2.5, 3, 3),
        [xs]: {
          padding: theme.spacing(2),
        },
      },
      mainSideBySide: {
        ...scrolling,
        flex: '0 0 auto',
        maxWidth: REDESIGN_DIALOG_SPLIT_MAIN_MAX,
      },
      // The well, a hairline at its edge with the main pane.
      aside: {
        boxSizing: 'border-box',
        minWidth: 0,
        margin: 0,
        padding: theme.spacing(2),
        backgroundColor: colors.well,
      },
      asideSideBySide: {
        ...scrolling,
        flex: '1 1 0',
        borderLeft: `1px solid ${colors.border}`,
      },
      asideStacked: {
        borderTop: `1px solid ${colors.border}`,
      },
      asideFlush: {
        '&&': {
          padding: 0,
        },
      },
    };
  },
  { name: 'LixRedesignDialogSplit' }
);

/**
 * Two panes in a RedesignDialog with `bodyPadding="none"` and `fullHeight`:
 * the `main` pane (the fields) and the `aside` (a PDF, a preview) on the
 * dialog's well. From md up they stand side by side, the main pane
 * `mainWidth` wide (at most 45% of the dialog on a medium screen), a hairline
 * between them, each scrolling on its own; below md they stack, main first,
 * and the dialog's body scrolls them. `asideLabel` names the aside (an
 * <aside> landmark), `mainLabel` the main pane if given. Both panes have the
 * body's padding; `asidePadding="none"` for an aside that fills its pane
 * edge to edge.
 * @example
 * <RedesignDialog open title={title} size="xl" fullHeight bodyPadding="none" onClose={close}>
 *   <RedesignDialogSplit
 *     main={<InvoiceFields />}
 *     aside={<InvoicePdf url={url} />}
 *     asideLabel={t('expense.invoice')}
 *   />
 * </RedesignDialog>
 * @param {Object} props
 * @param {React.ReactNode} props.main
 * @param {React.ReactNode} props.aside
 * @param {number} [props.mainWidth=440] px
 * @param {string} [props.asideLabel]
 * @param {string} [props.mainLabel]
 * @param {'normal'|'none'} [props.asidePadding='normal']
 */
function RedesignDialogSplit({
  main,
  aside,
  mainWidth,
  asideLabel,
  mainLabel,
  asidePadding,
  className,
}) {
  const classes = useStyles();
  const theme = useTheme();
  const wide = useMediaQuery(theme.breakpoints.up('md'), { noSsr: true });
  return (
    <div
      className={clsx(wide ? classes.sideBySide : classes.stacked, className)}
      data-redesign-dialog-split={wide ? 'side-by-side' : 'stacked'}
    >
      <div
        className={clsx(classes.main, wide && classes.mainSideBySide)}
        style={wide ? { width: mainWidth } : undefined}
        role={mainLabel ? 'region' : undefined}
        aria-label={mainLabel}
        data-redesign-dialog-pane="main"
      >
        {main}
      </div>
      <aside
        className={clsx(
          classes.aside,
          wide ? classes.asideSideBySide : classes.asideStacked,
          asidePadding === 'none' && classes.asideFlush
        )}
        aria-label={asideLabel}
        data-redesign-dialog-pane="aside"
      >
        {aside}
      </aside>
    </div>
  );
}

RedesignDialogSplit.propTypes = {
  /** The fields' pane. */
  main: PropTypes.node,
  /** The well's pane: a PDF, a preview. */
  aside: PropTypes.node,
  /** The main pane's width from md up (px); at most 45% of the dialog. */
  mainWidth: PropTypes.number,
  /** The aside's accessible name. */
  asideLabel: PropTypes.string,
  /** The main pane's accessible name (a region), if it needs one. */
  mainLabel: PropTypes.string,
  /** 'none' for an aside that fills its pane edge to edge. */
  asidePadding: PropTypes.oneOf(['normal', 'none']),
  className: PropTypes.string,
};

RedesignDialogSplit.defaultProps = {
  mainWidth: REDESIGN_DIALOG_SPLIT_MAIN_WIDTH,
  asidePadding: 'normal',
};

export default RedesignDialogSplit;
